import React, { useCallback, useEffect, useState } from "react";
import { VerseSettings } from "./VerseSettings";
import PropTypes from "prop-types";
import {
  AllowFlyingEnabledSwitch,
  CreateAndMoveObjectsEnabledSwitch,
  CreateCamerasEnabledSwitch,
  CreateDrawingsEnabledSwitch,
  CreateEmojisEnabledSwitch,
  Is360TourEnabledSwitch,
  PinObjectsEnabledSwitch,
  TextChatEnabledSwitch,
  TourPortalsEnabledSwitch,
  VoiceChatEnabledSwitch
} from "./RoomSettingsSidebarSwitches";
import { Input, Label, makeStyles, mergeClasses, Radio, RadioGroup, useId } from "@fluentui/react-components";
import { FormattedMessage, useIntl } from "react-intl";
import configs from "../../utils/configs";
import { InviteLinkInputField } from "./InviteLinkInputField";
import { useInviteUrl } from "./useInviteUrl";
import NumericInput from "../fluent/NumericInput";

const useClasses = makeStyles({
  input: {
    minWidth: "200px"
  },
  childInput: {
    marginLeft: "5px"
  },
  hidden: {
    display: "none"
  },
  wrapRow: {
    flexWrap: "wrap"
  },
  numberInput: {
    minWidth: "200px",
    "& input": {
      appearance: "textfield", // default behavior for number inputs
      // Apply styles for removing number input arrows in Webkit-based browsers
      "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
        appearance: "none",
        margin: 0
      },
      // For Firefox (removes spinner arrows)
      '&[type="number"]': {
        "-moz-appearance": "textfield"
      }
    }
  }
});

export function VerseSettingsContainer({ styles, scene, store, hubChannel, settings, setSettings }) {
  const { fetchingInvite, inviteUrl, revokeInvite } = useInviteUrl(hubChannel);

  return (
    <VerseSettings
      moderationChildren={<ModerationChildren styles={styles} settings={settings} setSettings={setSettings} />}
      accessChildren={
        <AccessChildren
          styles={styles}
          settings={settings}
          setSettings={setSettings}
          fetchingInvite={fetchingInvite}
          inviteUrl={inviteUrl}
          revokeInvite={revokeInvite}
        />
      }
      detailsChildren={<DetailsChildren styles={styles} settings={settings} setSettings={setSettings} />}
      advancedChildren={<AdvancedChildren styles={styles} settings={settings} setSettings={setSettings} />}
    ></VerseSettings>
  );
}

VerseSettingsContainer.propTypes = {
  scene: PropTypes.object.isRequired,
  store: PropTypes.object.isRequired,
  hubChannel: PropTypes.object.isRequired,
  styles: PropTypes.object.isRequired,
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired
};

function ModerationChildren({ styles, settings, setSettings }) {
  const onSettingsChange = useCallback(
    (key, value) => {
      setSettings(prevSettings => {
        const keys = key.split(".");
        const lastKey = keys.pop();

        const updatedSettings = keys.reduce((obj, key) => obj[key], prevSettings);
        updatedSettings[lastKey] = value;

        return { ...prevSettings };
      });
    },
    [setSettings]
  );

  useEffect(() => {
    if (!settings.member_permissions.spawn_and_move_media) {
      if (settings.member_permissions.spawn_camera !== false) {
        onSettingsChange("member_permissions.spawn_camera", false);
      }
      if (settings.member_permissions.pin_objects !== false) {
        onSettingsChange("member_permissions.pin_objects", false);
      }
    }
  }, [
    settings.member_permissions.spawn_and_move_media,
    onSettingsChange,
    settings.member_permissions.spawn_camera,
    settings.member_permissions.pin_objects
  ]);

  return (
    <>
      <div className={styles.row}>
        <VoiceChatEnabledSwitch
          voiceChatEnabled={settings.member_permissions.voice_chat}
          onVoiceChatEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.voice_chat", data.checked);
          }}
        />
      </div>
      <div className={styles.row}>
        <TextChatEnabledSwitch
          textChatEnabled={settings.member_permissions.text_chat}
          onTextChatEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.text_chat", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <CreateAndMoveObjectsEnabledSwitch
          createAndMoveObjectsEnabled={settings.member_permissions.spawn_and_move_media}
          onCreateAndMoveObjectsEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.spawn_and_move_media", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <CreateCamerasEnabledSwitch
          createCamerasEnabled={settings.member_permissions.spawn_camera}
          disabled={!settings.member_permissions.spawn_and_move_media}
          onCreateCamerasEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.spawn_camera", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <PinObjectsEnabledSwitch
          pinObjectsEnabled={settings.member_permissions.pin_objects}
          onPinObjectsEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.pin_objects", data.checked);
          }}
          disabled={!settings.member_permissions.spawn_and_move_media}
        />
      </div>

      <div className={styles.row}>
        <CreateDrawingsEnabledSwitch
          createDrawingsEnabled={settings.member_permissions.spawn_drawing}
          onCreateDrawingsEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.spawn_drawing", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <CreateEmojisEnabledSwitch
          createEmojisEnabled={settings.member_permissions.spawn_emoji}
          onCreateEmojisEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.spawn_emoji", data.checked);
          }}
        />
      </div>

      <div className={styles.row}>
        <AllowFlyingEnabledSwitch
          allowFlyingEnabled={settings.member_permissions.fly}
          onAllowFlyingEnabledChange={(_, data) => {
            onSettingsChange("member_permissions.fly", data.checked);
          }}
        />
      </div>
    </>
  );
}

ModerationChildren.propTypes = {
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
  styles: PropTypes.object
};

function DetailsChildren({ styles, settings, setSettings }) {
  const classes = useClasses();

  const onSettingsChange = useCallback(
    (key, value) => {
      setSettings(prevSettings => {
        const keys = key.split(".");
        const lastKey = keys.pop();

        const updatedSettings = keys.reduce((obj, key) => obj[key], prevSettings);
        updatedSettings[lastKey] = value;

        return { ...prevSettings };
      });
    },
    [setSettings]
  );

  const nameId = useId("name");
  const descriptionId = useId("description");
  const memberLimitId = useId("member_limit");
  const maxRoomSize = configs.feature("max_room_size");
  const intl = useIntl();

  return (
    <>
      <div className={mergeClasses(styles.row, classes.wrapRow)}>
        <Label htmlFor={nameId}>
          <FormattedMessage id="room-settings-sidebar.name" defaultMessage="Name" />
        </Label>
        <Input
          id={nameId}
          value={settings.name}
          onChange={e => onSettingsChange("name", e.target.value)}
          minLength={1}
          maxLength={64}
          required
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.name-placeholder",
            defaultMessage: "Verse name"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>
      <div className={mergeClasses(styles.row, classes.wrapRow)}>
        <Label htmlFor={descriptionId}>
          <FormattedMessage id="room-settings-sidebar.description" defaultMessage="Description" />
        </Label>
        <Input
          id={descriptionId}
          value={settings.description || ""}
          onChange={e => onSettingsChange("description", e.target.value)}
          maxLength={1000}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.description-placeholder",
            defaultMessage: "Enter Description"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>
      <div className={mergeClasses(styles.row, classes.wrapRow)}>
        <Label htmlFor={memberLimitId}>
          <FormattedMessage id="room-settings-sidebar.member-limit" defaultMessage="Member limit" />
        </Label>
        <Input
          id={memberLimitId}
          value={settings.room_size}
          onChange={e => onSettingsChange("room_size", e.target.value)}
          type="number"
          min={0}
          max={maxRoomSize}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.room-size-placeholder",
            defaultMessage: "Member Limit"
          })}
          appearance="filled-darker"
          className={classes.numberInput}
        />
      </div>
    </>
  );
}

DetailsChildren.propTypes = {
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
  styles: PropTypes.object
};

function AccessChildren({ styles, settings, setSettings, fetchingInvite, inviteUrl, revokeInvite }) {
  const onSettingsChange = useCallback(
    (key, value) => {
      setSettings(prevSettings => {
        const keys = key.split(".");
        const lastKey = keys.pop();

        const updatedSettings = keys.reduce((obj, key) => obj[key], prevSettings);
        updatedSettings[lastKey] = value;

        return { ...prevSettings };
      });
    },
    [setSettings]
  );

  const sharedLinkId = useId("shared_link");
  const inviteOnlyId = useId("invite_only");
  const revocableLinkId = useId("revocable_link");

  return (
    <>
      <div className={styles.row}>
        <Label htmlFor={sharedLinkId}>
          <FormattedMessage
            id="room-settings-sidebar.access-shared-link"
            defaultMessage="Shared link (Only those with the link can join)"
          />
        </Label>
        <Radio
          id={sharedLinkId}
          value="allow"
          checked={settings.entry_mode === "allow"}
          onChange={e => onSettingsChange("entry_mode", e.target.value)}
        ></Radio>
      </div>

      <div className={styles.row}>
        <Label htmlFor={inviteOnlyId}>
          <FormattedMessage
            id="room-settings-sidebar.access-invite"
            defaultMessage="Trustworthy (Only people with a revocable link can join)"
          />
        </Label>
        <Radio
          id={inviteOnlyId}
          value="invite"
          checked={settings.entry_mode === "invite"}
          onChange={e => onSettingsChange("entry_mode", e.target.value)}
        ></Radio>
      </div>
      {settings.entry_mode === "invite" && (
        <div className={styles.row}>
          <Label htmlFor={revocableLinkId}>
            <FormattedMessage id="invite-link-input-field.label" defaultMessage="Invite link" />
          </Label>
          <InviteLinkInputField fetchingInvite={fetchingInvite} inviteUrl={inviteUrl} onRevokeInvite={revokeInvite} />
        </div>
      )}
    </>
  );
}

AccessChildren.propTypes = {
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
  styles: PropTypes.object,
  fetchingInvite: PropTypes.bool,
  inviteUrl: PropTypes.string,
  revokeInvite: PropTypes.func.isRequired
};

function AdvancedChildren({ styles, settings, setSettings }) {
  const onSettingsChange = useCallback(
    (key, value) => {
      setSettings(prevSettings => {
        const keys = key.split(".");
        const lastKey = keys.pop();

        const updatedSettings = keys.reduce((obj, key) => {
          if (!obj[key]) obj[key] = {};
          return obj[key];
        }, prevSettings);

        updatedSettings[lastKey] = value;

        return { ...prevSettings };
      });
    },
    [setSettings]
  );

  const intl = useIntl();
  const classes = useClasses();

  const [portals, setPortals] = useState([...(settings.user_data?.portals ?? []), ""]);

  useEffect(() => {
    onSettingsChange(
      "user_data.portals",
      portals.filter(portal => portal !== "")
    );

    portals.every(portal => portal !== "") && setPortals([...portals, ""]);

    // Remove empty portals
    portals.filter(portal => portal === "").length > 1 && setPortals(portals.filter(portal => portal !== ""));
  }, [portals, onSettingsChange]);

  const handleFovChange = useCallback(
    value => {
      let fov = value;
      if (value !== null && value !== "") {
        if (value < 30) {
          fov = 30;
        } else if (value > 160) {
          fov = 160;
        }
      }
      onSettingsChange("user_data.fov", fov);
    },
    [onSettingsChange]
  );

  return (
    <>
      <div className={styles.row}>
        <Label htmlFor="user_data.leave_room_url">
          <FormattedMessage id="room-settings-sidebar.leave-room-url" defaultMessage="Leave Verse URL" />
        </Label>
        <Input
          id="user_data.leave_room_url"
          value={settings.user_data?.leave_room_url || ""}
          onChange={e => onSettingsChange("user_data.leave_room_url", e.target.value)}
          minLength={10}
          maxLength={255}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.leave-room-url-placeholder",
            defaultMessage: "Destination URL"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={styles.row}>
        <Label htmlFor="user_data.redirect_url">
          <FormattedMessage
            id="room-settings-sidebar.redirect-url"
            defaultMessage="URL to Redirect Users To (This not apply to Verse hosts)"
          />
        </Label>
        <Input
          id="user_data.redirect_url"
          value={settings.user_data?.redirect_url || ""}
          onChange={e => onSettingsChange("user_data.redirect_url", e.target.value)}
          minLength={10}
          maxLength={255}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.redirect-url-placeholder",
            defaultMessage: "Enter a URL"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={styles.row}>
        <Label htmlFor="user_data.fov">
          <FormattedMessage id="room-settings-sidebar.custom-camera-fov" defaultMessage="Custom Camera Field of View" />
        </Label>
        <NumericInput
          value={settings.user_data?.fov || ""}
          onChange={handleFovChange}
          min={30}
          max={160}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.custom-camera-fov-placeholder",
            defaultMessage: "FOV"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={styles.row}>
        <Is360TourEnabledSwitch
          is360TourEnabled={settings.user_data?.is_360_tour}
          onIs360TourEnabledChange={e => onSettingsChange("user_data.is_360_tour", e.target.checked)}
        />
      </div>

      <div className={styles.row}>
        <TourPortalsEnabledSwitch
          isTourPortalsEnabled={settings.user_data?.enable_tour_portals}
          onTourPortalsEnabledChange={e => onSettingsChange("user_data.enable_tour_portals", e.target.checked)}
        />
      </div>

      {portals.map((_, i) => (
        <div className={styles.row} key={`portal-input-${i}`}>
          {i === 0 ? (
            <Label htmlFor={`user_data.portals[${i}]`}>
              <FormattedMessage id="room-settings-sidebar.portal" defaultMessage="Portal URL" />
            </Label>
          ) : (
            <Label htmlFor={`user_data.portals[${i}]`} className={classes.childInput}>
              <FormattedMessage
                id="room-settings-sidebar.add-another-portal-placeholder"
                defaultMessage="Add another portal"
              />
            </Label>
          )}
          <Input
            id={`user_data.portals[${i}]`}
            value={portals[i] || ""}
            onChange={e => setPortals([...portals.slice(0, i), e.target.value, ...portals.slice(i + 1)])}
            minLength={10}
            maxLength={255}
            placeholder={intl.formatMessage({
              id: "room-settings-sidebar.portal-placeholder",
              defaultMessage: "Enter URL"
            })}
            appearance="filled-darker"
            className={classes.input}
          />
        </div>
      ))}

      <div className={classes.hidden}>
        <Label htmlFor="user_data.ticket_id">
          <FormattedMessage id="room-settings-sidebar.ticket_id-label" defaultMessage="Limit access by NFT Ticket ID" />
        </Label>
        <Input
          id="user_data.ticket_id"
          value={settings.user_data?.ticket_id || ""}
          onChange={e => onSettingsChange("user_data.ticket_id", e.target.value)}
          type="text"
          minLength={1}
          maxLength={64}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.ticket_id-placeholder",
            defaultMessage: "Enter NFT Ticket ID"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={classes.hidden}>
        <Label htmlFor="user_data.event_slug">
          <FormattedMessage id="room-settings-sidebar.event-slug-label" defaultMessage="Connect room to event" />
        </Label>
        <Input
          id="user_data.event_slug"
          value={settings.user_data?.event_slug || ""}
          onChange={e => onSettingsChange("user_data.event_slug", e.target.value)}
          type="text"
          minLength={1}
          maxLength={64}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.event-slug-placeholder",
            defaultMessage: "Enter the event slug"
          })}
          appearance="filled-darker"
          className={classes.input}
        />
      </div>

      <div className={classes.hidden}>
        <Label htmlFor="user_data.start_time">
          <FormattedMessage
            id="room-settings-sidebar.start-time-placeholder"
            defaultMessage="Set a start time for this room"
          />
        </Label>
        <Input
          id="user_data.start_time"
          value={settings.user_data?.start_time || ""}
          onChange={e => onSettingsChange("user_data.start_time", e.target.value)}
          type="hidden"
          minLength={1}
          maxLength={64}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.start-time-placeholder",
            defaultMessage: "Enter the start time"
          })}
          className={classes.input}
        />
      </div>

      <div className={classes.hidden}>
        <Label htmlFor="user_data.end_time">
          <FormattedMessage
            id="room-settings-sidebar.end-time-placeholder"
            defaultMessage="Set an end time for this room"
          />
        </Label>
        <Input
          id="user_data.end_time"
          value={settings.user_data?.end_time || ""}
          onChange={e => onSettingsChange("user_data.end_time", e.target.value)}
          type="hidden"
          minLength={1}
          maxLength={64}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.end-time-placeholder",
            defaultMessage: "Enter the end time"
          })}
          className={classes.input}
        />
      </div>

      <div className={classes.hidden}>
        <Label htmlFor="user_data.custom_host_url">
          <FormattedMessage id="room-settings-sidebar.custom-host-url" defaultMessage="Custom Host URL" />
        </Label>
        <Input
          id="user_data.custom_host_url"
          value={settings.user_data?.custom_host_url || ""}
          onChange={e => onSettingsChange("user_data.custom_host_url", e.target.value)}
          type="hidden"
          minLength={10}
          maxLength={255}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.custom-host-url-placeholder",
            defaultMessage: "Enter a Custom Host URL"
          })}
          className={classes.input}
        />

        <Label htmlFor="user_data.subdomain">
          <FormattedMessage id="room-settings-sidebar.subdomain" defaultMessage="Subdomain" />
        </Label>
        <Input
          id="user_data.subdomain"
          value={settings.user_data?.subdomain || ""}
          onChange={e => onSettingsChange("user_data.subdomain", e.target.value)}
          type="hidden"
          minLength={10}
          maxLength={255}
          placeholder={intl.formatMessage({
            id: "room-settings-sidebar.subdomain-placeholder",
            defaultMessage: "Enter a Subdomain"
          })}
          className={classes.input}
        />
      </div>
    </>
  );
}

AdvancedChildren.propTypes = {
  settings: PropTypes.object.isRequired,
  setSettings: PropTypes.func.isRequired,
  styles: PropTypes.object
};
